import { useEffect } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import type { PlaceForecast } from "../lib/weather";
import { displayWeatherCode, isSunnyDay, weatherKind } from "../lib/weather";

// Mappa del beltempo in stile TG: un campo meteo continuo (sole, nuvole, pioggia, neve, temporali)
// interpolato tra i luoghi visibili, con sole che ruota, nuvole che scorrono, pioggia e neve che cadono.
const CELL = 22;
const SIGMA = 85;
const REACH = 210;
const BUCKET = 130;
const COLORS: Array<[number, number, number]> = [[255, 196, 40], [140, 150, 165], [40, 100, 190], [200, 225, 245], [90, 55, 150]];
const ALPHAS = [.3, .3, .4, .34, .46];
// sole, nuvole, pioggia, neve, temporale
type Vec = [number, number, number, number, number];

const at = (values: ArrayLike<number>, index: number) => values[index] ?? 0;

function vectorOf(place: PlaceForecast, dayIndex: number): Vec {
  const day = place.days[dayIndex] ?? place.days[0];
  if (!day) return [0, 1, 0, 0, 0];
  const kind = weatherKind(displayWeatherCode(day));
  switch (kind) {
    case "clear": return [1, 0, 0, 0, 0];
    case "partly-cloudy": return isSunnyDay(day) ? [.8, .2, 0, 0, 0] : [.35, .65, 0, 0, 0];
    case "cloudy": return [0, 1, 0, 0, 0];
    case "fog": return [0, 1, 0, 0, 0];
    case "snow": return [0, .25, 0, .75, 0];
    case "storm": return [0, .15, .35, 0, .5];
    default: return [0, .25, .75, 0, 0];
  }
}

type Field = { cols: number; rows: number; w: number; h: number; cov: Float32Array; vec: Float32Array; tint: HTMLCanvasElement; buckets: Array<{ x: number; y: number; v: Vec }> };

function buildField(map: L.Map, places: PlaceForecast[], dayIndex: number): Field {
  const size = map.getSize();
  const cols = Math.ceil(size.x / CELL), rows = Math.ceil(size.y / CELL);
  const weight = new Float32Array(cols * rows), acc = new Float32Array(cols * rows * 5);
  const bucketMap = new Map<string, { x: number; y: number; n: number; v: Vec }>();
  for (const place of places) {
    const point = map.latLngToContainerPoint([place.latitude, place.longitude]);
    if (point.x < -REACH || point.x > size.x + REACH || point.y < -REACH || point.y > size.y + REACH) continue;
    const v = vectorOf(place, dayIndex);
    const c0 = Math.max(0, Math.floor((point.x - REACH) / CELL)), c1 = Math.min(cols - 1, Math.ceil((point.x + REACH) / CELL));
    const r0 = Math.max(0, Math.floor((point.y - REACH) / CELL)), r1 = Math.min(rows - 1, Math.ceil((point.y + REACH) / CELL));
    for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) {
      const dx = (c + .5) * CELL - point.x, dy = (r + .5) * CELL - point.y, d2 = dx * dx + dy * dy;
      if (d2 > REACH * REACH) continue;
      const w = Math.exp(-d2 / (2 * SIGMA * SIGMA)), i = r * cols + c;
      weight[i] = at(weight, i) + w;
      for (let k = 0; k < 5; k++) acc[i * 5 + k] = at(acc, i * 5 + k) + w * at(v, k);
    }
    if (point.x >= 0 && point.x <= size.x && point.y >= 0 && point.y <= size.y) {
      const key = `${Math.floor(point.x / BUCKET)}:${Math.floor(point.y / BUCKET)}`;
      const bucket = bucketMap.get(key) ?? { x: 0, y: 0, n: 0, v: [0, 0, 0, 0, 0] as Vec };
      bucket.x += point.x; bucket.y += point.y; bucket.n++;
      for (let k = 0; k < 5; k++) bucket.v[k] = at(bucket.v, k) + at(v, k);
      bucketMap.set(key, bucket);
    }
  }
  const cov = new Float32Array(cols * rows), vec = new Float32Array(cols * rows * 5);
  const tint = document.createElement("canvas");
  tint.width = cols; tint.height = rows;
  const tctx = tint.getContext("2d")!;
  const image = tctx.createImageData(cols, rows);
  for (let i = 0; i < cols * rows; i++) {
    const w = at(weight, i);
    if (w < .02) continue;
    cov[i] = 1 - Math.exp(-w * 2); const cv = cov[i]!;
    let r = 0, g = 0, b = 0, a = 0;
    for (let k = 0; k < 5; k++) {
      const share = at(acc, i * 5 + k) / w;
      vec[i * 5 + k] = share;
      const col = COLORS[k]!; r += share * col[0]; g += share * col[1]; b += share * col[2]; a += share * at(ALPHAS, k);
    }
    image.data[i * 4] = r; image.data[i * 4 + 1] = g; image.data[i * 4 + 2] = b; image.data[i * 4 + 3] = Math.min(255, cv * a * 255);
  }
  tctx.putImageData(image, 0, 0);
  const buckets = [...bucketMap.values()].map(bucket => ({ x: bucket.x / bucket.n, y: bucket.y / bucket.n, v: bucket.v.map(value => value / bucket.n) as Vec }));
  return { cols, rows, w: size.x, h: size.y, cov, vec, tint, buckets };
}

function cellAt(field: Field, x: number, y: number): number {
  const c = Math.floor(x / CELL), r = Math.floor(y / CELL);
  return c < 0 || r < 0 || c >= field.cols || r >= field.rows ? -1 : r * field.cols + c;
}

function cloudPath(ctx: CanvasRenderingContext2D, x: number, y: number, s: number) {
  ctx.beginPath();
  ctx.arc(x - s * .55, y + s * .1, s * .42, 0, Math.PI * 2);
  ctx.arc(x, y - s * .2, s * .55, 0, Math.PI * 2);
  ctx.arc(x + s * .6, y + s * .1, s * .45, 0, Math.PI * 2);
  ctx.rect(x - s * .55, y + s * .1, s * 1.15, s * .45);
  ctx.closePath();
}

function drawSun(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, t: number) {
  const glow = ctx.createRadialGradient(x, y, s * .2, x, y, s * 2.4);
  glow.addColorStop(0, "rgba(255,214,70,.55)"); glow.addColorStop(1, "rgba(255,214,70,0)");
  ctx.fillStyle = glow; ctx.beginPath(); ctx.arc(x, y, s * 2.4, 0, Math.PI * 2); ctx.fill();
  ctx.save(); ctx.translate(x, y); ctx.rotate(t * .35);
  ctx.strokeStyle = "rgba(255,190,20,.95)"; ctx.lineWidth = s * .13; ctx.lineCap = "round";
  for (let i = 0; i < 10; i++) {
    const length = s * (.34 + .1 * Math.sin(t * 2.2 + i));
    ctx.rotate(Math.PI / 5); ctx.beginPath(); ctx.moveTo(0, -s * .8); ctx.lineTo(0, -s * .8 - length); ctx.stroke();
  }
  ctx.restore();
  const body = ctx.createRadialGradient(x - s * .2, y - s * .2, s * .1, x, y, s * .62);
  body.addColorStop(0, "#fff3a8"); body.addColorStop(1, "#ffb400");
  ctx.fillStyle = body; ctx.strokeStyle = "rgba(255,255,255,.9)"; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.arc(x, y, s * .62, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
}

function drawCloud(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, t: number, fill: string, drift = 1) {
  const dx = Math.sin(t * .5 + x) * 4 * drift;
  ctx.save(); ctx.shadowColor = "rgba(20,30,50,.3)"; ctx.shadowBlur = 8; ctx.shadowOffsetY = 3;
  ctx.fillStyle = fill; cloudPath(ctx, x + dx, y, s); ctx.fill();
  ctx.restore();
  ctx.strokeStyle = "rgba(255,255,255,.75)"; ctx.lineWidth = 1.5; cloudPath(ctx, x + dx, y, s); ctx.stroke();
}

function drawBucket(ctx: CanvasRenderingContext2D, bucket: Field["buckets"][number], t: number) {
  const { x, y, v } = bucket;
  const top = v.indexOf(Math.max(...v));
  const s = 26;
  if (top === 0) { drawSun(ctx, x, y, s, t); return; }
  if (top === 1) { drawCloud(ctx, x, y, s, t, "rgba(236,240,246,.92)"); return; }
  const dark = top === 4;
  drawCloud(ctx, x, y - 6, s, t, dark ? "rgba(70,62,100,.95)" : "rgba(150,165,185,.95)");
  if (top === 2 || top === 4) {
    ctx.strokeStyle = "rgba(120,190,255,.95)"; ctx.lineWidth = 2.4; ctx.lineCap = "round";
    for (let i = 0; i < 5; i++) {
      const phase = (t * 1.4 + i * .27) % 1;
      const px = x - s * .6 + i * s * .3 - phase * 6, py = y + 10 + phase * 22;
      ctx.globalAlpha = 1 - phase; ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px - 3, py + 8); ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }
  if (top === 4 && Math.sin(t * 3.1) > .55) {
    ctx.fillStyle = "#ffe14a"; ctx.strokeStyle = "#fff"; ctx.lineWidth = 1.2;
    ctx.beginPath(); ctx.moveTo(x + 2, y + 6); ctx.lineTo(x - 7, y + 24); ctx.lineTo(x, y + 22); ctx.lineTo(x - 4, y + 38); ctx.lineTo(x + 9, y + 18); ctx.lineTo(x + 2, y + 19); ctx.closePath(); ctx.fill(); ctx.stroke();
  }
  if (top === 3) {
    ctx.fillStyle = "#fff";
    for (let i = 0; i < 5; i++) {
      const phase = (t * .5 + i * .21) % 1;
      ctx.globalAlpha = 1 - phase; ctx.beginPath(); ctx.arc(x - s * .6 + i * s * .3 + Math.sin(t + i) * 3, y + 10 + phase * 24, 2.4, 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;
  }
}

type Drop = { x: number; y: number; speed: number; len: number };
type Spark = { x: number; y: number; phase: number };

export function WeatherFx({ places, dayIndex }: { places: PlaceForecast[]; dayIndex: number }) {
  const map = useMap();
  useEffect(() => {
    const pane = map.getPane("weatherFx") ?? map.createPane("weatherFx");
    pane.style.zIndex = "450"; pane.style.pointerEvents = "none";
    const canvas = L.DomUtil.create("canvas", "weather-fx", pane) as HTMLCanvasElement;
    const ctx = canvas.getContext("2d")!;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let field: Field | undefined, dirty = true, frame = 0, last = performance.now(), time = 0, dpr = 1;
    const drops: Drop[] = Array.from({ length: 320 }, () => ({ x: Math.random(), y: Math.random(), speed: 380 + Math.random() * 260, len: 9 + Math.random() * 9 }));
    const flakes: Drop[] = Array.from({ length: 180 }, () => ({ x: Math.random(), y: Math.random(), speed: 40 + Math.random() * 50, len: 1.6 + Math.random() * 1.8 }));
    const sparks: Spark[] = Array.from({ length: 46 }, () => ({ x: Math.random(), y: Math.random(), phase: Math.random() }));
    const place = () => {
      const size = map.getSize();
      dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = size.x * dpr; canvas.height = size.y * dpr;
      canvas.style.width = `${size.x}px`; canvas.style.height = `${size.y}px`;
      L.DomUtil.setPosition(canvas, map.containerPointToLayerPoint([0, 0]));
      dirty = true;
    };
    const markDirty = () => { L.DomUtil.setPosition(canvas, map.containerPointToLayerPoint([0, 0])); dirty = true; };
    const draw = (now: number) => {
      const dt = Math.min(.05, (now - last) / 1000); last = now; time += dt;
      if (dirty) { field = buildField(map, places, dayIndex); dirty = false; }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (field) {
        ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = "high";
        ctx.globalAlpha = .85 + .15 * Math.sin(time * 1.2);
        ctx.drawImage(field.tint, 0, 0, field.cols * CELL, field.rows * CELL);
        ctx.globalAlpha = 1;
        for (const spark of sparks) {
          spark.phase += dt * .5;
          if (spark.phase >= 1) { spark.phase = 0; spark.x = Math.random(); spark.y = Math.random(); }
          const x = spark.x * field.w, y = spark.y * field.h, i = cellAt(field, x, y);
          if (i < 0 || at(field.vec, i * 5) * at(field.cov, i) < .4) continue;
          const a = Math.sin(spark.phase * Math.PI), s = 3 + 4 * a;
          ctx.fillStyle = `rgba(255,255,255,${.85 * a})`;
          ctx.beginPath(); ctx.moveTo(x, y - s); ctx.lineTo(x + s * .25, y - s * .25); ctx.lineTo(x + s, y); ctx.lineTo(x + s * .25, y + s * .25); ctx.lineTo(x, y + s); ctx.lineTo(x - s * .25, y + s * .25); ctx.lineTo(x - s, y); ctx.lineTo(x - s * .25, y - s * .25); ctx.closePath(); ctx.fill();
        }
        ctx.lineCap = "round"; ctx.lineWidth = 1.5;
        for (const drop of drops) {
          drop.y += drop.speed * dt / field.h; drop.x -= drop.speed * .22 * dt / field.w;
          if (drop.y > 1 || drop.x < 0) { drop.y = -.02; drop.x = Math.random() * 1.2; }
          const x = drop.x * field.w, y = drop.y * field.h, i = cellAt(field, x, y);
          if (i < 0) continue;
          const rain = (at(field.vec, i * 5 + 2) + at(field.vec, i * 5 + 4)) * at(field.cov, i);
          if (rain < .25) continue;
          ctx.strokeStyle = `rgba(170,215,255,${Math.min(.85, rain)})`;
          ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + drop.len * .22, y - drop.len); ctx.stroke();
        }
        for (const flake of flakes) {
          flake.y += flake.speed * dt / field.h; flake.x += Math.sin(time * 1.5 + flake.len * 9) * 14 * dt / field.w;
          if (flake.y > 1) { flake.y = -.02; flake.x = Math.random(); }
          const x = flake.x * field.w, y = flake.y * field.h, i = cellAt(field, x, y);
          if (i < 0 || at(field.vec, i * 5 + 3) * at(field.cov, i) < .25) continue;
          ctx.fillStyle = "rgba(255,255,255,.92)"; ctx.beginPath(); ctx.arc(x, y, flake.len, 0, Math.PI * 2); ctx.fill();
        }
        for (const bucket of field.buckets) {
          const i = cellAt(field, bucket.x, bucket.y);
          ctx.globalAlpha = i < 0 ? 0 : Math.min(.9, .35 + at(field.cov, i));
          drawBucket(ctx, bucket, time);
        }
        ctx.globalAlpha = 1;
      }
      if (!reduced) frame = requestAnimationFrame(draw);
    };
    const redraw = () => { markDirty(); if (reduced) { cancelAnimationFrame(frame); frame = requestAnimationFrame(draw); } };
    place();
    map.on("move zoom viewreset", redraw).on("resize", place);
    frame = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frame);
      map.off("move zoom viewreset", redraw).off("resize", place);
      canvas.remove();
    };
  }, [map, places, dayIndex]);
  return null;
}
